<template>
    <VueApexCharts
        ref="reportChart"
        type="line"
        height="220"
        :options="chartOptions"
        :series="seriesArray"
    />
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import {computed, onMounted, ref, watch} from "@vue/composition-api";
import {getComponentAsHtmlForTooltip, getDiscreteMarkers, setBarChartRoundedTop} from "../../../helpers/chart";
import ReportPeriodChartTooltip from "./ReportPeriodChartTooltip.vue";
import {formatTimeDuration} from "../../../plugins/formatting";
import {$t} from "../../../i18n";


export default {
    name: 'ReportPeriodChart',
    components: {
        ReportPeriodChartTooltip,
        VueApexCharts,
    },
    props: {
        graphSegments: {
            type: Array,
            default: null,
        }
    },
    setup(props) {
        const reportChart = ref(null)
        const barRadiusPixel = 8
        const mainYAxisBuffer = 2 // spacing between graph max
        const barchartColumnsCountToSwitchShortLabel = 14
        const showUpperLabelUntilColumnCount = 21
        const strokeColor = "var(--v-white-base)"

        const getXAxisLabels = computed(() => {
            // Show label with sub label if sub label exists; otherwise show only the label
            let labels = []
            const barColumnsCount = props.graphSegments.length
            for (const segment of props.graphSegments) {
                if (segment.label && segment.sub_label) {
                    const label = getVisibleLabel(segment, barColumnsCount)
                    const subLabel = segment.sub_label
                    labels.push([label, subLabel])
                } else if (segment.label) {
                    labels.push(segment.label)
                }
            }

            return labels
        })

        const getVisibleLabel = (segment, barColumnsCount) => {
            // Show the label if there are fewer than 14 records; otherwise, show the short label if it exists
            let visibleLabel = segment.label

            if (barColumnsCount > barchartColumnsCountToSwitchShortLabel) {
                if (segment.short_label) {
                    visibleLabel = segment.short_label
                }
            }
            return visibleLabel
        }

        const getBillable = computed(() => {
            return props.graphSegments.map(item => item.billable / 3600);
        })

        const getNonBillable = computed(() => {
            return props.graphSegments.map(item => item.non_billable / 3600);
        })

        const getModifiedTasksCount = computed(() => {
            return getCompletedTasksCount.value.map(item => item * getSecondYAxisMultiplier.value)
        })

        const getCompletedTasksCount = computed(() => {
            return props.graphSegments.map(item => item.completed_tasks_count);
        })

        const getYAxisMaxHours = computed(() => {
            // Get Y axis max by calculating max hours + some buffer
            const totals = props.graphSegments.map(obj => obj.billable + obj.non_billable);
            const maxDuration = Math.max(...totals);
            const durationHours = Math.ceil(maxDuration / 3600)

            return durationHours + mainYAxisBuffer
        })

        const getYaxisMaxTasksCount = computed(() => {
            // Get invisible Y axis max
            const tasksCounts = props.graphSegments.map(obj => obj.completed_tasks_count)
            return Math.max(...tasksCounts)
        })

        const getSecondYAxisMultiplier = computed(() => {
            // Calculate the second Y-axis multiplier based on the main Y-axis.
            // The line chart is not related to the bar chart, so to avoid overlapping data labels on the graph,
            // we move the line chart halfway down by using (max hours / 2).
            const tasksMax = getYaxisMaxTasksCount.value
            if (!tasksMax) {
                return 0
            }

            const maxHours = getYAxisMaxHours.value / 2
            return maxHours / tasksMax
        })

        const formatBarChartUpperLabel = (index) => {
            const segmentsCount = props.graphSegments.length
            if (segmentsCount > showUpperLabelUntilColumnCount) {
                return ''
            }

            const segment = props.graphSegments[index]
            const billable = segment ? segment.billable : 0
            const nonBillable = segment ? segment.non_billable : 0
            const total = billable + nonBillable

            if (!total) {
                return ''
            }
            return formatTimeDuration(total);
        }

        const formatYAxisTimeLabels = (val) => {
            return `${Math.floor(val)}${$t('h')}`
        }

        const seriesArray = computed(() => {
            return [
                {
                    type: 'bar',
                    name: 'Billable',
                    data: getBillable.value,
                    color: 'var(--v-tretiary-base)',
                },
                {
                    type: 'bar',
                    name: 'Non-billable',
                    data: getNonBillable.value,
                    color: 'var(--v-g4-base)',
                },
                {
                    type: 'line',
                    name: 'Tasks done',
                    data: getModifiedTasksCount.value,
                    color: 'var(--v-tretiary-lighten1)',
                }
            ];
        })


        const chartOptions = computed(() => {
            let discreteMarkers = []

            // LINE CHART: Find markers to hide when the 'Tasks done' value is 0
            const taskDoneSeriesIndex = seriesArray.value.findIndex(series => series.name === 'Tasks done')
            if (taskDoneSeriesIndex !== -1) {
                const seriesData = seriesArray.value[taskDoneSeriesIndex].data
                discreteMarkers = getDiscreteMarkers(seriesData, taskDoneSeriesIndex)
            }


            return {
                chart: {
                    type: 'line',
                    fontFamily: 'Inter, sans-serif',
                    stacked: true,
                    zoom: {
                        enabled: false
                    },
                    events: {
                        updated: function (chartContext, config) {
                            initializeChart();
                        }
                    },
                    toolbar: {
                        show: false
                    }
                },
                stroke: {
                    colors: [strokeColor],
                    width: [0.5, 0.5, 4]
                },
                xaxis: {
                    categories: getXAxisLabels.value,
                    labels: {
                        style: {
                            fontSize: '11px',
                            fontWeight: 600
                        }
                    },
                    tickPlacement: 'on'
                },
                yaxis: [
                    {
                        seriesName: 'hours',
                        min: 0,
                        max: getYAxisMaxHours.value,
                        tickAmount: 5,
                        labels: {
                            formatter: function (val) {
                                return formatYAxisTimeLabels(val)
                            },
                            style: {
                                fontSize: '11px',
                                fontWeight: 600
                            }
                        }
                    },
                ],
                legend: {
                    show: false
                },
                plotOptions: {
                    // BAR CHART
                    bar: {
                        horizontal: false,
                        borderRadius: barRadiusPixel,
                        borderRadiusApplication: "end",
                        borderRadiusWhenStacked: "last",
                        dataLabels: {
                            rotate: 90,
                            total: {
                                enabled: true,
                                offsetX: 0,
                                style: {
                                    fontSize: '13px',
                                    fontWeight: 600
                                },
                                formatter: function (val, setup) {
                                    return formatBarChartUpperLabel(setup.dataPointIndex)
                                }
                            }
                        }
                    },
                },
                markers: {
                    // LINE CHART: task count circle
                    size: 12,
                    shape: 'circle',
                    colors: 'var(--v-tretiary-lighten1)',
                    strokeColors: strokeColor,
                    strokeWidth: 1,
                    hover: {
                        size: 12
                    },
                    discrete: discreteMarkers
                },
                dataLabels: {
                    // LINE CHART: task count label
                    enabled: true,
                    enabledOnSeries: [taskDoneSeriesIndex],
                    style: {
                        colors: ['var(--v-main-base)'],
                        fontSize: '11px',
                        fontWeight: 600
                    },
                    formatter: function (val, setup) {
                        if (!val) {
                            return ''
                        }

                        return Math.round(val / getSecondYAxisMultiplier.value)
                    },
                    background: {
                        enabled: false
                    },
                    dropShadow: {
                        enabled: false
                    },
                    offsetY: 1
                },
                tooltip: {
                    custom: function ({dataPointIndex}) {
                        const segment = props.graphSegments[dataPointIndex]
                        const propsData = {
                            segment: segment
                        }
                        return getComponentAsHtmlForTooltip(ReportPeriodChartTooltip, propsData)
                    }
                }
            }
        });

        const initializeChart = () => {
            if (reportChart.value) {
                setBarChartRoundedTop(reportChart, barRadiusPixel);
            }
        };

        onMounted(() => {
            initializeChart();
        })


        watch (() => reportChart.value, (newVal, oldVal) => {
            if (oldVal !== newVal && newVal) {
                initializeChart()
            }
        }, { immediate: true })


        return {
            reportChart,
            seriesArray,
            chartOptions,
        }
    }
}

</script>

<style scoped lang="scss">
::v-deep .apexcharts-xaxis .apexcharts-xaxis-texts-g .apexcharts-xaxis-label tspan:nth-child(2) {
    fill: var(--v-g4-base)
}
</style>